import { clamp } from '@racemap/utilities/math';
import { useGesture } from '@use-gesture/react';
import { type MutableRefObject, useRef } from 'react';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import type { Bounds } from '../InfoSheet/SVGChart';
import {
  hitTestDot,
  unprojectX,
  type ViewRange,
  type ZoomRange,
  zoomAroundPointer,
} from './heightChartUtils';

type UseHeightChartGesturesArgs = {
  chartBounds: { left: number };
  boundsRef: MutableRefObject<Bounds | null>;
  renderInfoBuffer: RenderInfoBuffer;
  viewRange: ViewRange;
  dataMaxX: number;
  isPoiHighlighted: boolean;
  setHighlightedOffset: (offset: number) => void;
  setHighlighted: (id: string | null) => void;
  toggleSelectedStarter: (id: string) => void;
  setZoom: (zoom: ZoomRange | null) => void;
};

export function useHeightChartGestures({
  chartBounds,
  boundsRef,
  renderInfoBuffer,
  viewRange,
  dataMaxX,
  isPoiHighlighted,
  setHighlightedOffset,
  setHighlighted,
  toggleSelectedStarter,
  setZoom,
}: UseHeightChartGesturesArgs) {
  function updateHighlightedOffsetFromX(hoverX: number) {
    if (boundsRef.current == null) return;
    const offset = unprojectX(hoverX, viewRange, boundsRef.current);
    setHighlightedOffset(clamp(viewRange.minX, offset, viewRange.maxX));
  }

  const pinchStartRangeRef = useRef<number | null>(null);

  return useGesture(
    {
      onMove: ({ xy: [x] }) => {
        if (boundsRef.current == null) return;

        const hoverX = x - chartBounds.left;
        updateHighlightedOffsetFromX(hoverX);
        if (!isPoiHighlighted) {
          const starter = hitTestDot(renderInfoBuffer, boundsRef.current, viewRange, hoverX);
          if (starter != null) {
            setHighlighted(starter.id);
            return;
          }
          setHighlighted(null);
        }
      },
      onDrag: ({ tap, last, xy: [x] }) => {
        if (boundsRef.current == null) return;

        if (last) {
          setHighlightedOffset(-1);
        } else {
          updateHighlightedOffsetFromX(x - chartBounds.left);
        }

        if (tap) {
          const starter = hitTestDot(
            renderInfoBuffer,
            boundsRef.current,
            viewRange,
            x - chartBounds.left,
          );
          if (starter != null) {
            toggleSelectedStarter(starter.id);
          }
        }
      },
      onHover: ({ active }) => {
        if (!active) {
          setHighlightedOffset(-1);
          if (!isPoiHighlighted) {
            setHighlighted(null);
          }
        }
      },
      onWheel: ({ event, delta: [, dy], ctrlKey }) => {
        if (boundsRef.current == null || dataMaxX <= 0) return;
        event.preventDefault();
        const hoverX = (event as WheelEvent).clientX - chartBounds.left;
        const pointerDataX = unprojectX(hoverX, viewRange, boundsRef.current);
        const sensitivity = ctrlKey ? 0.02 : 0.005;
        const factor = Math.exp(dy * sensitivity);
        setZoom(zoomAroundPointer(viewRange, pointerDataX, factor, dataMaxX));
      },
      onPinchStart: () => {
        pinchStartRangeRef.current = viewRange.maxX - viewRange.minX;
      },
      onPinch: ({ origin: [originX], offset: [scale] }) => {
        if (boundsRef.current == null || dataMaxX <= 0 || pinchStartRangeRef.current == null) {
          return;
        }
        if (scale == null || !Number.isFinite(scale) || scale <= 0) return;
        const pointerDataX = unprojectX(originX - chartBounds.left, viewRange, boundsRef.current);
        const currentSpan = viewRange.maxX - viewRange.minX;
        if (currentSpan <= 0) return;
        const targetSpan = pinchStartRangeRef.current / scale;
        const factor = targetSpan / currentSpan;
        setZoom(zoomAroundPointer(viewRange, pointerDataX, factor, dataMaxX));
      },
      onPinchEnd: () => {
        pinchStartRangeRef.current = null;
      },
    },
    {
      drag: { filterTaps: true },
      wheel: { eventOptions: { passive: false } },
      pinch: { scaleBounds: { min: 0.1, max: 10 } },
    },
  );
}
